import React, { useState, useEffect } from 'react';
import { getData, putData } from '../Services/api';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';

const CustomerUpdate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '',
    customer_type: '',
    phone_numbers: '',
    address: '',
    latitude: '',
    longitude: '',
    isApproved: 1,
    notes: [],
    routeId: ''
  });

  const [routes, setRoutes] = useState([]);

  // Fetch existing customer and routes for their salesperson
  useEffect(() => {
    const fetchCustomerAndRoutes = async () => {
      try {
        const res = await getData(`/customers/details/${id}`);
        if (res.success) {
          const customerData = res.data;
          setForm({
            ...customerData,
            notes: customerData.notes && Array.isArray(customerData.notes)
              ? customerData.notes
              : []
          });

          let routeList = [];
          let fetchedSalespersonRoutes = false;

          // 1. Try to fetch routes for the customer's salesperson
          if (customerData.createdByUserId) {
            try {
              const userRes = await getData(`/users/details/${customerData.createdByUserId}`);
              if (userRes.status && userRes.data && userRes.data.routes) {
                routeList = userRes.data.routes || [];
                fetchedSalespersonRoutes = true;
              }
            } catch (err) {
              console.error('Failed to fetch salesperson routes:', err);
            }
          }

          // 2. Fallback to all routes if we couldn't fetch salesperson's routes
          if (!fetchedSalespersonRoutes) {
            try {
              const allRoutesRes = await getData('/routes/all');
              if (allRoutesRes.status) {
                routeList = allRoutesRes.data || [];
              }
            } catch (err) {
              console.error('Failed to fetch fallback routes:', err);
            }
          }

          // 3. Ensure the customer's currently assigned route is in the list
          if (customerData.route && customerData.route.id) {
            const hasCurrentRoute = routeList.some(r => r.id === customerData.route.id);
            if (!hasCurrentRoute) {
              routeList.unshift(customerData.route);
            }
          }

          setRoutes(routeList);
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to fetch customer');
      }
    };
    fetchCustomerAndRoutes();
  }, [id]);

  // Handle input
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Handle note input
  const handleNoteChange = (index, e) => {
    const updatedNotes = [...form.notes];
    updatedNotes[index][e.target.name] = e.target.value;
    setForm({ ...form, notes: updatedNotes });
  };

  // Add note
  const addNote = () => {
    setForm({
      ...form,
      notes: [...form.notes, { title: '', description: '' }]
    });
  };

  // Remove note
  const removeNote = (index) => {
    const updatedNotes = [...form.notes];
    updatedNotes.splice(index, 1);
    setForm({ ...form, notes: updatedNotes });
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        notes: form.notes.map((note) => ({
          title: note.title,
          description: note.description,
          createdAt: note.createdAt || new Date().toISOString(),
          createdBy: note.createdBy || 1 // <- current user ID (auth se lena hoga)
        }))
      };

      const res = await putData(`/customers/${id}`, payload);

      if (res.success) {
        toast.success('Customer updated successfully');
        navigate('/customer/customer-list');
      } else {
        toast.error(res.message || 'Update failed');
      }
    } catch (err) {
      console.error(err);
      toast.error('Server error');
    }
  };

  return (
    <div className="pc-container">
      <div className="pc-content">
        <h2>Edit Customer</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label>Name</label>
            <input type="text" className="form-control" name="name" value={form.name} onChange={handleChange} required />
          </div>

          <div className="mb-3">
            <label>Customer Type</label>
            <input type="text" className="form-control" name="customer_type" value={form.customer_type} onChange={handleChange} required />
          </div>

          <div className="mb-3">
            <label>Phone Numbers (comma separated)</label>
            <input type="text" className="form-control" name="phone_numbers" value={form.phone_numbers} onChange={handleChange} />
          </div>

          <div className="mb-3">
            <label>Address</label>
            <input type="text" className="form-control" name="address" value={form.address} onChange={handleChange} />
          </div>

          <div className="mb-3">
            <label>Latitude</label>
            <input type="text" className="form-control" name="latitude" value={form.latitude} onChange={handleChange} />
          </div>

          <div className="mb-3">
            <label>Longitude</label>
            <input type="text" className="form-control" name="longitude" value={form.longitude} onChange={handleChange} />
          </div>

          {/* Notes Section */}
          <div className="mb-3">
            <label>Notes</label>
            {form.notes.map((note, index) => (
              <div key={index} className="border p-2 mb-2 rounded">
                <input
                  type="text"
                  className="form-control mb-2"
                  name="title"
                  placeholder="Note Title"
                  value={note.title}
                  onChange={(e) => handleNoteChange(index, e)}
                />
                <textarea
                  className="form-control mb-2"
                  name="description"
                  placeholder="Note Description"
                  value={note.description}
                  onChange={(e) => handleNoteChange(index, e)}
                ></textarea>
                <button type="button" className="btn btn-danger btn-sm" onClick={() => removeNote(index)}>
                  Remove
                </button>
              </div>
            ))}
            <button type="button" className="btn btn-secondary" onClick={addNote}>
              + Add Note
            </button>
          </div>

          {/* Route Dropdown */}
          <div className="mb-3">
            <label>Route</label>
            <select
              className="form-control"
              name="routeId"
              value={form.routeId}
              onChange={handleChange}
              required
            >
              <option value="">-- Select Route --</option>
              {routes.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.route_name || `Route ${r.id}`}
                </option>
              ))}
            </select>
          </div>

          <button type="submit" className="btn btn-primary">Update</button>
        </form>
      </div>
    </div>
  );
};

export default CustomerUpdate;
